import pclnExperimentation from '@pcln/experimentation'
import useBootstrapData from '@/hooks/useBootstrapData'
import {
  transformAbandonedHotelApiResponseToProps,
  transformAbandonedCarApiResponseToProps
} from '@/components/TripActivity/utils'
import useSeti from '@/hooks/useSeti'
import useRecentSearchCard, {
  SearchProductList
} from '@/hooks/useRecentSearchCard'
import useMostRecentFutureBooking from '@/hooks/useMostRecentFutureBooking'
import { parse, isAfter, isToday } from 'date-fns'
import useAbandonedSelections from './useAbandonedSelections'
import {
  AbandonedCarSelectionApiResponse,
  AbandonedHotelSelectionApiResponse,
  AbandonedSelectionApiResponse
} from '../types'

function isTripDateValid(tripDate: string, formatString: string) {
  const parseTripDate = parse(tripDate, formatString, new Date())

  return isToday(parseTripDate) || isAfter(parseTripDate, new Date())
}

function isValidAbandonedHotelSelection(
  selection: AbandonedSelectionApiResponse
): selection is AbandonedHotelSelectionApiResponse {
  return (
    'hotelURL' in selection &&
    isTripDateValid(selection.checkInDate, 'EEE, MMM d, yyyy')
  )
}

function isValidAbandonedCarSelection(
  selection: AbandonedSelectionApiResponse
): selection is AbandonedCarSelectionApiResponse {
  return (
    'carURL' in selection &&
    isTripDateValid(selection.pickupDateTime, "EEE, MMM dd, yyyy 'at' h:mma")
  )
}

function useTripActivityData(productType: SearchProductList) {
  const {
    webstats: { clientGUID },
    appName,
    appVersion,
    signInStatus: { signedIn }
  } = useBootstrapData()

  const abandonedSelectionsResponse = useAbandonedSelections(
    clientGUID,
    {
      appName,
      appVersion
    },
    signedIn,
    productType
  )

  const showRCAbandonedExperiment =
    useSeti('HP_RC_SELECTIONS', false) === 'VARIANT'

  const { mostRecentFutureBooking } = useMostRecentFutureBooking()
  const data = useRecentSearchCard(
    clientGUID,
    { appName, appVersion },
    productType
  )
  const atMostFiveRecentSearchCards = data.length > 5 ? data.slice(0, 5) : data

  if (mostRecentFutureBooking) {
    return {
      abandonedSelection: null,
      atMostFiveRecentSearchCards
    }
  }

  const abandonedSelection =
    abandonedSelectionsResponse.find(selection => {
      if (isValidAbandonedHotelSelection(selection)) {
        return true
      }

      if (isValidAbandonedCarSelection(selection)) {
        pclnExperimentation.fireImpression('HP_RC_SELECTIONS')
        return showRCAbandonedExperiment
      }

      return false
    }) ?? null

  if (abandonedSelection) {
    return {
      abandonedSelection:
        'hotelURL' in abandonedSelection
          ? transformAbandonedHotelApiResponseToProps(abandonedSelection)
          : transformAbandonedCarApiResponseToProps(abandonedSelection),
      atMostFiveRecentSearchCards
    }
  }

  return {
    abandonedSelection: null,
    atMostFiveRecentSearchCards
  }
}

export default useTripActivityData
